import React, { useState, useEffect, FC, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { RoomHeader } from './RoomHeader';
import { playSE } from './SoundEffect';
import "./Cardate.css";
import { PostAchievement } from './ModuleLinkFirebase';
import { ShieldBan, Sword } from 'lucide-react';
import { ScoreBoard } from './ScoreBoard';

export type roomDataType = {
  roomID: string,
  roomName: string,
  password: string | null,
  players: [
    { socketId: string | null, uid: string | null, pNum: 1, dispName: string | null, ready: boolean, icon: string | null, tag: string | null },
    { socketId: string | null, uid: string | null, pNum: 2, dispName: string | null, ready: boolean, icon: string | null, tag: string | null },
  ],
  matchPoint: number,
  cardCount: boolean,
  inPlay: boolean,
  gameState: {
    cardSet: number[],
    remains: {
      p1: number[],
      p2: number[],
    },
    actions: {
      p1: number | null,
      p2: number | null,
    },
    attackPlayer: 1 | 2,
    scores: { p1: number, p2: number },
  },
  createdAt: number,
  lastActivity: number,
  isBusy: boolean,
}

type judgeResultType = {
  actions: { p1: number, p2: number }, win: number, wincon: number
};

type riteType = {
  deck1: number[], deck2: number[], atkP: 1 | 2
}

interface ChildProps {
  socket: Socket;
  setErrorState: React.Dispatch<React.SetStateAction<string | null>>;
  roomData: roomDataType | null;
  setRoomData: React.Dispatch<React.SetStateAction<roomDataType | null>>;
}

export const Cardate: FC<ChildProps> = ({ socket, setErrorState, roomData, setRoomData }) => {
  const [isBusy, setBusy] = useState<boolean>(false);
  const [rite, setRite] = useState<riteType | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [judgeResult, setJudgeResult] = useState<judgeResultType | null>(null);
  const [judgeCountdown, setJudgeCountdown] = useState<number>(0);

  const [deckType, setDeckType] = useState<string>("std");
  const [matchPoint, setMatchPoint] = useState<number>(3);
  const [cardCount, setCardCount] = useState<boolean>(true);

  const roomDataRef = useRef<roomDataType | null>(null);
  const [displayGame, setDisplayGame] = useState<boolean>(false);
  const [displayedScore, setDisplayedScore] = useState<{ p1: number, p2: number }>({ p1: 0, p2: 0 });
  const [displayedAttackPlayer, setDisplayedAttackPlayer] = useState<number>(0);
  const [displayedRemainCards, setDisplayedRemainCards] = useState<{ p1: number[], p2: number[] }>({ p1: [], p2: [] });
  const [displayedBG, setDisplayedBG] = useState<number>(0);
  const [displayMatchResult, setDisplayMatchResult] = useState<boolean>(false);
  const [displayGameResult, setDisplayGameResult] = useState<boolean>(false);
  const [displayCutIn, setDisplayCutIn] = useState<boolean>(false);
  const [winHistory, setWinHistory] = useState<number[]>([]);

  useEffect(() => {
    socket.on("game_update", (data) => {
      console.log("Received game update:", data);
      setRoomData(data);
    });

    socket.on("judge", (data) => {
      console.log("Judge result:", data);
      setJudgeResult(data);
      setJudgeCountdown(3);
      if (data.win == 0) {
        setTimeout(() => {
          setJudgeResult(null);
          setBusy(false);
          setDisplayedAttackPlayer(roomDataRef.current?.gameState.attackPlayer ?? 0);
          setDisplayedRemainCards(roomDataRef.current?.gameState.remains ?? displayedRemainCards);
        }, 3000);
      }
    });

    socket.on("action_error", (data) => {
      setErrorState(data.message);
    });

    socket.on("flip_rite", (data: riteType) => {
      console.log("Rite:", data);
      setRite(data);
    });

    return () => {
      socket.off("game_update");
      socket.off("judge");
      socket.off("action_error");
    };
  }, [socket]);

  useEffect(() => {
    if (judgeCountdown == 0 && judgeResult && judgeResult.win !== 0) {
      setWinHistory(prev => [...prev, judgeResult.wincon * (judgeResult.win == myPNum ? 1 : -1)]);
      switch (judgeResult.wincon) {
        case 1:
        case 2:
        case 3:
          playSE("attack");
          setDisplayCutIn(true);
          setTimeout(() => {
            setDisplayMatchResult(true);
            setDisplayCutIn(false);
          }, (900));
          break;
        case 4:
          playSE("failed");
          setTimeout(() => {
            setDisplayMatchResult(true);
            setDisplayCutIn(false);
          }, (100));
          break;
        case 5:
          playSE("attack");
          setDisplayCutIn(true);
          setTimeout(() => {
            setDisplayMatchResult(true);
            setDisplayCutIn(false);
          }, (1200));
          setTimeout(() => {
            playSE("attack");
          }, 150);
          setTimeout(() => {
            playSE("attack");
          }, 300);
          break;
      }
    }
    if (judgeCountdown == 0 && judgeResult && judgeResult.win === 0) {
      playSE("cardturnover1");
    }
    if (judgeCountdown <= 0) {
      setDisplayedScore(prev => {
        prev.p1 = roomData?.gameState.scores.p1 ?? prev.p1;
        prev.p2 = roomData?.gameState.scores.p2 ?? prev.p2;
        return prev;
      });
      return;
    }
    setTimeout(() => {
      setJudgeCountdown(prev => prev - 1);
    }, 300);
  }, [judgeCountdown]);

  useEffect(() => {
    setJudgeResult(null);
    setBusy(false);
    playSE("cardturnover1");
    if (!rite) return;
    setTimeout(() => {
      setRite(null);
      setDisplayedAttackPlayer(roomDataRef.current?.gameState.attackPlayer ?? 0);
      setDisplayedRemainCards(roomDataRef.current?.gameState.remains ?? displayedRemainCards);
      setDisplayedBG(0);
    }, 2000);
  }, [rite]);

  useEffect(() => {
    roomDataRef.current = roomData;
    if (!displayGame && roomData?.inPlay === true) setDisplayGame(roomData.inPlay);
    if (roomData?.players.some(p => p.socketId == null)) setDisplayGame(roomData.inPlay);
  }, [roomData]);

  useEffect(() => {
    setDisplayedBG(displayedRemainCards.p1.filter(i => i !== 0).length);
  }, [displayedRemainCards]);
  const colorTop = [
    "#D4FCFC", "#33285B", "#33285B", "#423473", "#7F476F", "#BF5C6B", "#FF7066", "#FA8873", "#F7987C", "#F2AF88", "#DDB7A1", "#CBBEB7", "#B5C7D1", "#9ED0EC", "#8FD6FF", "#99DCFE", "#A4E2FE", "#AEE7FD", "#B5EBFD", "#BDEFFC", "#C6F4FC", "#CDF8FC"
  ];
  const colorCenter = [
    "#EFFFEB", "#D9CCF0", "#D9CCF0", "#D9CCF0", "#E2D9FF", "#E2D9FF", "#FFDEDE", "#FFDEDE", "#FFDEDE", "#FFF2DB", "#FFF2DB", "#FFF2DB", "#FFF2DB", "#F0FFE6", "#F0FFE6", "#F0FFE6", "#F0FFE6", "#F0FFE6", "#EFFFEB", "#EFFFEB", "#EFFFEB", "#EFFFEB"
  ];
  const colorBottom = [
    "#DBFCD4", "#544C69", "#544C69", "#615A75", "#6D6780", "#816675", "#91666C", "#A86560", "#C1867B", "#D49E8E", "#CCB28E", "#C5C38E", "#BFD38E", "#B7E68E", "#B1F58F", "#B7F698", "#BDF7A2", "#C3F8AD", "#C8F9B6", "#CDFABD", "#D1FBC3", "#D5FBCA"
  ];

  const sendGameReady = () => {
    setJudgeCountdown(-1);
    setDisplayGameResult(false);
    setDisplayedScore({ p1: 0, p2: 0 });
    setWinHistory([]);
    socket.emit("room_ready", { ready: true, deckType: deckType, matchPoint: matchPoint, cardCount: cardCount });
  }

  const sendMatchReady = () => {
    setJudgeCountdown(-1);
    if (roomData?.inPlay) {
      socket.emit("match_ready", { ready: true });
    } else {
      sendAchievement();
      setDisplayGameResult(true);
    }
  }

  const sendAction = (n: number) => {
    setSelectedCard(n);
    socket.emit("do_action", { a: n });
  };

  function findPNumByID(pid?: string) {
    let pNum = 0;
    const players = roomData?.players;
    if (!players || !pid) {
      return pNum;
    } else {
      players.forEach((entry) => {
        if (entry.socketId === pid) {
          pNum = entry.pNum;
        }
      });
      return pNum;
    }
  }

  const getOpponentPNum = () => {
    const myPNum = findPNumByID(socket.id);
    return myPNum === 1 ? 2 : 1;
  }

  const getPlayerName = (pNum: 1 | 2) => {
    if (!roomData) return ``;
    const player = roomData.players.find(p => p.pNum === pNum);
    if (player?.tag) {
      return <span className={player.tag}>{player.dispName}</span>
    }
    return player?.dispName ?? "";
  }

  const getPlayerIcon = (pNum: 1 | 2) => {
    if (!roomData) return ``;
    const player = roomData.players.find(p => p.pNum === pNum);
    return player?.icon ?? "sample";
  }

  const getPlayerReady = (pNum: 1 | 2) => {
    if (!roomData) return "";
    const player = roomData.players.find(p => p.pNum === pNum);
    if (pNum === 1 && player?.ready) return "準備OK!";
    if (pNum === 2 && player?.ready) return "準備OK!";
    return "";
  }

  const XORReady = () => {
    const a = getPlayerReady(1);
    const b = getPlayerReady(2);
    return (a || b) && !(a && b);
  }

  const myPNum = findPNumByID(socket.id);
  const opponentPNum = getOpponentPNum();
  const myAction = roomData?.gameState.actions[`p${myPNum}` as 'p1' | 'p2'];
  const opponentAction = roomData?.gameState.actions[`p${opponentPNum}` as 'p1' | 'p2'];

  const renderCard = (value: number) => {
    if (value === 100) return 'X';
    if (value === 1) return 'A';
    return value;
  }

  const rite_animation = () => {
    const totalCards = rite?.deck1.length ?? 1;
    const overlapOffset = totalCards > 1 ? Math.min(40, 600 / totalCards) : 0;

    const op2 = rite?.deck2.map((n, i) => (
      <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-row:3/4] [grid-column:3/4] justify-self-center animate-[rite-opp_0.2s_ease-out]"
        key={i}
        style={{ transform: `translateX(${overlapOffset * (i - (totalCards - 1) * 0.5)}px)`, animationDelay: `${i * 0.2}s`, zIndex: "1" }}>
        <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(n)}.webp`} className="w-full h-full rounded-[0.6rem]" />
      </div>
    ));

    const my1 = rite?.deck1.map((n, i) => (
      <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-row:4/5] [grid-column:3/4] justify-self-center animate-[rite-my_0.2s_ease-out]"
        key={i}
        style={{ transform: `translateX(${overlapOffset * (i - (totalCards - 1) * 0.5)}px)`, animationDelay: `${i * 0.2}s`, zIndex: "1" }}>
        <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(n)}.webp`} className="w-full h-full rounded-[0.6rem]" />
      </div>
    ));

    const op1 = rite?.deck1.map((n, i) => (
      <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-row:3/4] [grid-column:3/4] justify-self-center animate-[rite-opp_0.2s_ease-out]"
        key={i}
        style={{ transform: `translateX(${overlapOffset * (i - (totalCards - 1) * 0.5)}px)`, animationDelay: `${i * 0.2}s`, zIndex: "1" }}>
        <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(n)}.webp`} className="w-full h-full rounded-[0.6rem]" />
      </div>
    ));

    const my2 = rite?.deck2.map((n, i) => (
      <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-row:4/5] [grid-column:3/4] justify-self-center animate-[rite-my_0.2s_ease-out]"
        key={i}
        style={{ transform: `translateX(${overlapOffset * (i - (totalCards - 1) * 0.5)}px)`, animationDelay: `${i * 0.2}s`, zIndex: "1" }}>
        <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(n)}.webp`} className="w-full h-full rounded-[0.6rem]" />
      </div>
    ));

    return <>
      <style>
        {`
        @keyframes rite-opp{
          from{transform:translateY(-100%);}
        }
        @keyframes rite-my{
          from{transform:translateY(100%);}
        }
        `}
      </style>
      <div className="relative flex items-center justify-center min-h-[120px] [grid-column:1/6] [grid-row:2/3]">
        <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center transition-all duration-300 border-[3px] border-white/30 shadow-[0_4px_6px_rgba(0,0,0,0.3)]" style={{ zIndex: "1" }}>
          <div className="text-[2.5rem] text-gray-400 font-bold">?</div>
        </div>
      </div>
      <div className="relative flex items-center justify-center min-h-[120px] [grid-column:1/6]" style={{ gridRow: "3/4", gridColumn: "3/4" }}>{myPNum == 1 ? op2 : op1}</div>
      <div className="relative flex items-center justify-center min-h-[120px] [grid-column:1/6]" style={{ gridRow: "4/5", gridColumn: "3/4" }}>{myPNum == 1 ? my1 : my2}</div>
      <div className="relative flex items-center justify-center min-h-[120px] [grid-column:1/6] [grid-row:5/6]">
        <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center transition-all duration-300 border-[3px] border-white/30 shadow-[0_4px_6px_rgba(0,0,0,0.3)]" style={{ zIndex: "1" }}>
          <div className="text-[2.5rem] text-gray-400 font-bold">?</div>
        </div>
      </div>
    </>;
  }

  const my_hand = () => {
    if (!roomData) return <></>;
    let remainCard = null;
    const pNum = findPNumByID(socket.id);
    if (pNum == 1) remainCard = roomData.gameState.remains.p1;
    if (pNum == 2) remainCard = roomData.gameState.remains.p2;
    if (!remainCard) return <></>;

    const cards = remainCard
      .map((n, index) => ({ n, index }))
      .filter(card => card.n > 0);
    const totalCards = cards.length;
    const parentWidth = (document.getElementById("root")?.clientWidth ?? 600) - 60;
    const overlapOffset = totalCards > 1 ? Math.min(40, Math.min(600, parentWidth) / totalCards) : 0;

    return cards.map((card, i) => (
      <div
        className={`w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] hover:translate-y-[-10px] hover:shadow-[0_10px_20px_rgba(0,0,0,0.5)] hover:border-[#fbbf24] ${myAction !== null ? 'opacity-50 cursor-not-allowed hover:translate-y-0 hover:shadow-[0_4px_6px_rgba(0,0,0,0.3)] hover:border-white/50' : ''}`}
        key={i}
        onClick={() => {
          if (!isBusy && myAction === null) {
            setBusy(true);
            sendAction(card.index);
            playSE("cardpick1");
          }
        }}
        style={{ transform: `translateX(${overlapOffset * (i - (totalCards - 1) * 0.5)}px)`, zIndex: `${i}` }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(roomData.gameState.cardSet[card.index] ?? 0)}.webp`} className="w-full h-full rounded-[0.6rem]" />
      </div>
    ))
  }

  const opponent_hand = () => {
    if (!roomData) return <></>;
    let remainCard = null;
    const oppPNum = opponentPNum;
    if (oppPNum == 1) remainCard = roomData.gameState.remains.p1;
    if (oppPNum == 2) remainCard = roomData.gameState.remains.p2;
    if (!remainCard) return <></>;

    const cards = remainCard
      .map((n, index) => ({ n, index }))
      .filter(card => card.n > 0);
    const totalCards = cards.length;
    const parentWidth = (document.getElementById("root")?.clientWidth ?? 600) - 60;
    const overlapOffset = totalCards > 1 ? Math.min(40, Math.min(600, parentWidth) / totalCards) : 0;

    return cards.map((n, index) => (
      <div className="w-20 h-[80px] md:h-[110px] absolute bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center transition-all duration-300 border-[3px] border-white/30 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
        key={index}
        style={{ transform: `translateX(${overlapOffset * (index - (totalCards - 1) * 0.5)}px)`, zIndex: `${index}` }}>
        <div className="text-[2.5rem] text-gray-400 font-bold">?</div>
      </div>
    ))
  }

  const judge_animation = () => {
    if (!roomData || !judgeResult) return <></>;
    return <>{judgeCountdown > 0 ?
      <p className="m-0 [grid-row:3/5] [grid-column:3/4] text-slate-700 text-3xl font-bold text-center content-center">{judgeCountdown}</p> : <>
        <div className={`w-20 h-[80px] md:h-[110px] absolute inset-0 m-auto bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-column:2/5] md:[grid-column:3/4] justify-self-center animate-[flip-card_0.5s_forwards] ${opponentPNum == 1 ? "[grid-row:3/4]" : "[grid-row:4/5]"}`}>
          <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(roomData.gameState.cardSet[judgeResult?.actions.p1] ?? 0)}.webp`} className="w-full h-full rounded-[0.6rem]" />
        </div>
        <div className={`w-20 h-[80px] md:h-[110px] absolute inset-0 m-auto bg-gradient-to-br from-white to-gray-200 rounded-xl flex items-start justify-start cursor-pointer transition-all duration-300 border-[3px] border-white/50 shadow-[0_4px_6px_rgba(0,0,0,0.3)] [grid-column:2/5] md:[grid-column:3/4] justify-self-center animate-[flip-card_0.5s_forwards] ${opponentPNum == 2 ? "[grid-row:3/4]" : "[grid-row:4/5]"}`}>
          <img src={`${process.env.PUBLIC_URL}/img/card_${renderCard(roomData.gameState.cardSet[judgeResult?.actions.p2] ?? 0)}.webp`} className="w-full h-full rounded-[0.6rem]" />
        </div>
      </>
    }</>
  };

  const getWinConditionText = (wincon: number) => {
    return `(勝利条件${wincon}による)`
  }

  const remainingCardsDisplay = (pNum: 1 | 2) => {
    const remains = displayedRemainCards[`p${pNum}`];
    const cardSet = roomData?.gameState.cardSet;
    if (!cardSet) return <></>;

    let sortedRemains: { [key: number]: number } = {};
    cardSet.forEach(e => {
      sortedRemains[e] = 0;
    });
    remains.forEach((flag, i) => {
      if (flag) sortedRemains[cardSet[i]]++;
    });

    const toArr: [string, number][] = Object.entries(sortedRemains);
    const rows: number = Math.ceil(cardSet.length / 2);
    return (
      <div className={`w-fit h-fit self-center justify-self-center [grid-column:5/6] gap-x-1 gap-y-[2px] md:gap-1 ${pNum === myPNum ? "[grid-row:4/5] md:[grid-row:4/6]" : "[grid-row:3/4] md:[grid-row:2/4]"}`}
        style={{ display: "grid", gridTemplateColumns: "repeat(2,16px)", gridTemplateRows: `repeat(${rows}, 8px)` }}>
        {toArr.map((e, i) => {
          const key = Number(e[0]);
          switch (key) {
            case 100:
              return <div key={i} className={`w-4 h-2 rounded bg-[#ccc] opacity-20 ${e[1] > 0 ? "!bg-white !opacity-100 animate-[blink_3.4s_infinite]" : ""}`}
                style={{ gridColumn: "1/2", gridRow: "1/2" }}></div>;
            case 1:
              return (
                <React.Fragment key={i}>
                  <div className={`w-4 h-2 rounded bg-[#ccc] opacity-20 ${e[1] > 0 ? "!bg-white !opacity-100 animate-[blink_3.4s_infinite]" : ""}`}
                    style={{ gridColumn: "1/2", gridRow: "2/3" }}></div>
                  <div className={`w-4 h-2 rounded bg-[#ccc] opacity-20 ${e[1] > 1 ? "!bg-white !opacity-100 animate-[blink_3.4s_infinite]" : ""}`}
                    style={{ gridColumn: "2/3", gridRow: "2/3" }}></div>
                </React.Fragment>
              );
            default:
              return (
                <React.Fragment key={i}>
                  <div className={`w-4 h-2 rounded bg-[#ccc] opacity-20 ${e[1] > 0 ? "!bg-white !opacity-100 animate-[blink_3.4s_infinite]" : ""}`}
                    style={{ gridColumn: "1/2", gridRow: `${rows - key + 2}/${rows - key + 3}` }}></div>
                  <div className={`w-4 h-2 rounded bg-[#ccc] opacity-20 ${e[1] > 1 ? "!bg-white !opacity-100 animate-[blink_3.4s_infinite]" : ""}`}
                    style={{ gridColumn: "2/3", gridRow: `${rows - key + 2}/${rows - key + 3}` }}></div>
                </React.Fragment>
              );
          }
        })}
      </div>
    );
  }

  function sendAchievement() {
    const opp = roomData?.players.find(p => p.pNum === opponentPNum);
    const pass = judgeResult?.win === myPNum && opp?.tag === "dev" ? 'ZGVmZWF0TmVpbGx5' : 'eWFtaW5vZ2U='
    PostAchievement(atob(pass));
  }

  if (!roomData) {
    return (
      <section className="h-dvh box-border bg-gradient-to-b from-[#c2a59f] via-[#e3d3bf] to-[#e8cfa5] flex items-center justify-center p-4">
        <div className="text-white text-3xl text-center">接続中...</div>
      </section>
    );
  }

  return (
    <section className="h-full rounded-xl box-border flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: `linear-gradient(180deg, ${colorTop[displayedBG]}, ${colorCenter[displayedBG]} 50%, ${colorBottom[displayedBG]} 100%)`, transition: "background 1s" }}>
      <style>
        {`
        @keyframes flip-card {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); }
          to { transform: scale(1); }
        }
        @keyframes cut-in {
          0%, 100% { transform: rotateX(90deg); }
          20%, 80% { transform: rotateX(0); }
        }
        `}
      </style>
      <div className="w-full max-w-[1200px] h-full box-border flex flex-col justify-center">
        <RoomHeader socket={socket} roomData={roomData}></RoomHeader>

        {!displayGame && (
          <div className="flex-1 flex flex-col items-center justify-center bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
            <div className="p-4 md:px-8 py-4 rounded-lg bg-slate-600/30 border border-slate-600/60 grid grid-cols-2 gap-x-8 gap-y-2 text-center">
              {getPlayerName(1) !== "" ?
                <p>{getPlayerName(1)}</p>
                : <p className="animate-[blink_1s_infinite] text-gray-500">参加待ち...</p>}
              {getPlayerName(2) !== "" ?
                <p>{getPlayerName(2)}</p>
                : <p className="animate-[blink_1s_infinite] text-gray-500">参加待ち...</p>}
              <img className="w-[120px] object-contain border-4 border-white rounded-lg" src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(1)}.webp`} alt="player1" />
              <img className="w-[120px] object-contain border-4 border-white rounded-lg" src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(2)}.webp`} alt="player2" />
              <p className="block h-6 text-xs text-red-500">{getPlayerReady(1)}</p>
              <p className="block h-6 text-xs text-red-500">{getPlayerReady(2)}</p>
            </div>
            {findPNumByID(socket.id) === 1 ?
              <div className="grid grid-cols-3 gap-1 mt-6">
                <p className="col-span-4 my-1">マッチ数</p>
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2 box-border bg-zinc-400/50 text-white/50 font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base ${matchPoint === 3 ? "!bg-gradient-to-br !from-[#0575E6] !to-[#021B79] !text-white -translate-y-0.5 shadow-[0_4px_12px_rgba(116,165,212,0.3)]" : ""}`}>
                  3本先取
                  <input type="radio" name="mp" value="3" checked={matchPoint === 3} className="hidden" onChange={(e) => setMatchPoint(Number(e.target.value))} />
                </label>
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2 box-border bg-zinc-400/50 text-white/50 font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base ${matchPoint === 5 ? "!bg-gradient-to-br !from-[#0575E6] !to-[#021B79] !text-white -translate-y-0.5 shadow-[0_4px_12px_rgba(116,165,212,0.3)]" : ""}`}>
                  5本先取
                  <input type="radio" name="mp" value="5" checked={matchPoint === 5} className="hidden" onChange={(e) => setMatchPoint(Number(e.target.value))} />
                </label>
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2 box-border bg-zinc-400/50 text-white/50 font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base ${matchPoint === 11 ? "!bg-gradient-to-br !from-[#0575E6] !to-[#021B79] !text-white -translate-y-0.5 shadow-[0_4px_12px_rgba(116,165,212,0.3)]" : ""}`}>
                  11本先取
                  <input type="radio" name="mp" value="11" checked={matchPoint === 11} className="hidden" onChange={(e) => setMatchPoint(Number(e.target.value))} />
                </label>
                <p className="col-span-4 my-1">残りカード</p>
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2 box-border bg-zinc-400/50 text-white/50 font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base ${cardCount === true ? "!bg-gradient-to-br !from-[#0575E6] !to-[#021B79] !text-white -translate-y-0.5 shadow-[0_4px_12px_rgba(116,165,212,0.3)]" : ""}`}>
                  表示(推奨)
                  <input type="radio" name="cardcount" value="true" checked={cardCount === true} className="hidden" onChange={() => setCardCount(true)} />
                </label>
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-400/50 text-white/50 font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base ${cardCount === false ? "!bg-gradient-to-br !from-[#0575E6] !to-[#021B79] !text-white -translate-y-0.5 shadow-[0_4px_12px_rgba(116,165,212,0.3)]" : ""}`}>
                  非表示
                  <input type="radio" name="cardcount" value="false" checked={cardCount === false} className="hidden" onChange={() => setCardCount(false)} />
                </label>
              </div>
              : <p className="opacity-80">対戦相手が設定しています</p>}
            {roomData.players.filter(p => p.socketId !== null).length === 2 && (
              <button className="mt-4 px-12 py-4 text-xl font-bold text-white bg-gradient-to-br from-[#10b981] to-[#059669] border-none rounded-xl cursor-pointer transition-all duration-300 hover:bg-gradient-to-br hover:from-[#059669] hover:to-[#047857] hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(16,185,129,0.3)]"
                onClick={sendGameReady}>ゲーム開始</button>
            )}
          </div>
        )}

        {displayGame && (
          <>
            <div className={`grid gap-1 grid-rows-[2fr_repeat(4,3fr)_2fr] grid-cols-[repeat(5,1fr)] bg-white/5 backdrop-blur-md rounded-2xl p-1 md:p-8 border border-white/20 ${!rite && displayedAttackPlayer ? (myPNum == displayedAttackPlayer ? "!bg-gradient-to-b !from-[#3b82f699] !via-transparent !to-[#ef444499]" : "!bg-gradient-to-b !from-[#ef444499] !via-transparent !to-[#3b82f699]") : ""}`}>
              {!rite && displayedAttackPlayer ?
                <>
                  <Sword color={((myPNum == displayedAttackPlayer && myAction !== null) || (myPNum !== displayedAttackPlayer && opponentAction !== null) && !judgeResult) ? "#cdfabd" : "white"} size={64} className={`absolute inset-0 m-auto [grid-column:1/3] md:[grid-column:5/6] ${myPNum == displayedAttackPlayer ? "[grid-row:4/5] md:[grid-row:6/7]" : "[grid-row:3/4] md:[grid-row:1/2]"}`}></Sword>
                  <ShieldBan color={((myPNum !== displayedAttackPlayer && myAction !== null) || (myPNum == displayedAttackPlayer && opponentAction !== null) && !judgeResult) ? "#cdfabd" : "white"} size={64} className={`absolute inset-0 m-auto [grid-column:1/3] md:[grid-column:5/6] ${myPNum == displayedAttackPlayer ? "[grid-row:3/4] md:[grid-row:1/2]" : "[grid-row:4/5] md:[grid-row:6/7]"}`}></ShieldBan>
                </>
                : <></>}
              <img className="absolute h-[45%] md:h-[200px] object-contain top-2 left-2 opacity-60 md:opacity-100" src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(myPNum == 1 ? 2 : 1)}.webp`} alt="opponent" />
              <img className="absolute h-[45%] md:h-[200px] object-contain bottom-2 left-2 opacity-60 md:opacity-100" src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(myPNum == 1 ? 1 : 2)}.webp`} alt="me" />
              <div className="flex flex-row justify-center items-center gap-8 text-white text-xl m-0 font-semibold text-center [grid-column:1/5] md:[grid-column:1/3] [grid-row:1/2]">
                {getPlayerName(myPNum == 1 ? 2 : 1)}
              </div>
              <div className="flex flex-row justify-center items-center gap-8 text-white text-xl m-0 font-semibold text-center [grid-column:1/5] md:[grid-column:1/3] [grid-row:6/7]">
                {getPlayerName(myPNum == 1 ? 1 : 2)}
              </div>
              <p className="text-slate-700 text-5xl m-0 font-semibold text-center content-center [grid-column:5/6] [grid-row:1/2] md:[grid-column:1/2] md:[grid-row:3/4]">{myPNum == 1 ? displayedScore.p2 : displayedScore.p1}</p>
              <p className="hidden md:block text-slate-700 text-3xl m-0 font-semibold text-center content-center [grid-column:1/2] [grid-row:3/5]">vs</p>
              <p className="text-slate-700 text-5xl m-0 font-semibold text-center content-center [grid-column:5/6] [grid-row:6/7] md:[grid-column:1/2] md:[grid-row:4/5]">{myPNum == 1 ? displayedScore.p1 : displayedScore.p2}</p>

              {rite ? rite_animation() : <>
                <div className="relative flex items-center justify-center min-h-[100px] md:min-h-[120px] [grid-column:1/6] [grid-row:2/3]">
                  {opponent_hand()}
                </div>

                {getPlayerReady(1) && getPlayerReady(2) && !judgeResult &&
                  <div className="hidden md:block [grid-row:6/7] [grid-column:3/5] text-white font-semibold text-center content-center whitespace-nowrap px-8 py-4 bg-white/10 rounded-xl border-2 border-white/30 animate-[blink_1s_infinite]">
                    {myPNum == displayedAttackPlayer ? "攻撃：" : "守備："}
                    {myAction === null ? 'カードを選択してください' : '選択を待っています...'}
                  </div>
                }

                {judge_animation()}

                <div className="relative flex items-center justify-center min-h-[100px] md:min-h-[120px] [grid-column:1/6] [grid-row:5/6]">
                  {my_hand()}
                </div>
                {XORReady() &&
                  <>
                    <p className="[grid-column:4/5] [grid-row:1/2] text-xs text-red-500 content-center text-center">{getPlayerReady(myPNum == 1 ? 2 : 1)}</p>
                    <p className="[grid-column:4/5] [grid-row:6/7] text-xs text-red-500 content-center text-center">{getPlayerReady(myPNum == 1 ? 1 : 2)}</p>
                  </>
                }
                {judgeCountdown == 0 && judgeResult?.win !== 0 && !getPlayerReady(myPNum == 1 ? 1 : 2) &&
                  <button className="z-[100] [grid-row:5/6] [grid-column:2/5] md:[grid-row:6/7] md:[grid-column:4/5] place-self-center px-6 py-4 text-xl font-bold text-white bg-gradient-to-br from-[#10b981] to-[#059669] border-none rounded-xl cursor-pointer transition-all duration-300 hover:bg-gradient-to-br hover:from-[#059669] hover:to-[#047857] hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(16,185,129,0.3)]"
                    onClick={sendMatchReady}>準備OK!</button>
                }
                {roomData.cardCount && remainingCardsDisplay(1)}
                {roomData.cardCount && remainingCardsDisplay(2)}
              </>}
            </div>
          </>
        )}

        {displayCutIn && (
          (judgeResult && judgeResult?.wincon) === 5 ? <>
            <div className={`cut-in-5 ${judgeResult?.win == roomData.gameState.attackPlayer ? "" : "inv"}`}>
              <img src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(judgeResult?.win === 1 ? 1 : 2)}_cut.webp`} className="h-full" alt="cut-in" />
            </div>
            <div className={`cut-in-5 ${judgeResult?.win == roomData.gameState.attackPlayer ? "" : "inv"}`}>
              <img src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(judgeResult?.win === 1 ? 1 : 2)}_cut.webp`} className="h-full" alt="cut-in" />
            </div>
            <div className={`cut-in-5 ${judgeResult?.win == roomData.gameState.attackPlayer ? "" : "inv"}`}>
              <img src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(judgeResult?.win === 1 ? 1 : 2)}_cut.webp`} className="h-full" alt="cut-in" />
            </div>
          </> :
            <div className={`cut-in ${judgeResult?.win == roomData.gameState.attackPlayer ? "" : "inv"}`}>
              <img src={`${process.env.PUBLIC_URL}/img/${getPlayerIcon(judgeResult?.win === 1 ? 1 : 2)}_cut.webp`} className="h-full" alt="cut-in" />
            </div>
        )}

        {displayMatchResult && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] animate-[fadeIn_0.3s]">
            <div className={`bg-gradient-to-br ${judgeResult?.win === myPNum ? "from-amber-400 to-yellow-600" : "from-slate-600 to-slate-800"} rounded-3xl p-12 text-center border-[3px] border-white/30 animate-[scaleIn_0.3s]`}>
              <h2 className={`text-5xl mb-4 font-bold ${judgeResult?.win === myPNum ? 'text-yellow-50 [text-shadow:0_0_20px_rgba(251,191,36,0.8)]' : 'text-slate-300 [text-shadow:0_0_10px_rgba(100,116,139,0.5)]'}`}>
                {judgeResult?.win === myPNum ? '勝ち!' : '負け…'}
              </h2>
              <p className="text-white text-xl m-0">{getWinConditionText(judgeResult?.wincon ?? 0)}</p>
              <button className="mt-6 px-12 py-4 text-xl font-bold text-white bg-gradient-to-br from-emerald-500 to-emerald-700 border-none rounded-xl cursor-pointer transition-all duration-300 hover:from-emerald-600 hover:to-emerald-800 hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(16,185,129,0.3)]"
                onClick={() => { setDisplayMatchResult(false) }}>OK</button>
            </div>
          </div>
        )}

        {displayGameResult && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] animate-[fadeIn_0.3s]">
            <div className={`max-w-full bg-gradient-to-br ${judgeResult?.win === myPNum ? "from-amber-400 to-yellow-600" : "from-slate-600 to-slate-800"} rounded-3xl p-12 text-center border-[3px] border-white/30 animate-[scaleIn_0.3s]`}>
              <h2 className={`text-5xl mb-4 font-bold ${judgeResult?.win === myPNum ? 'text-yellow-50 [text-shadow:0_0_20px_rgba(251,191,36,0.8)]' : 'text-slate-300 [text-shadow:0_0_10px_rgba(100,116,139,0.5)]'}`}>
                {judgeResult?.win === myPNum ? 'You Win!' : 'You Lose...'}
              </h2>
              <ScoreBoard player1Name={getPlayerName(myPNum == 1 ? 2 : 1)} player2Name={getPlayerName(myPNum == 1 ? 1 : 2)} winHistory={winHistory}></ScoreBoard>
              <a href='https://twitter.com/intent/tweet?hashtags=%E3%83%90%E3%83%89%E3%82%A2%E3%82%BD&related=nei_ion&text=%E2%97%86%E2%97%86%E2%97%86%E9%97%87%E9%8D%8B%E3%83%9C%E3%83%89%E3%82%B2%E3%83%BB%E3%83%A4%E3%83%9F%E3%83%8E%E3%82%B2%E2%97%86%E2%97%86%E2%97%86%0A%E3%83%AB%E3%83%BC%E3%83%AB%E3%82%92%E7%90%86%E8%A7%A3%E3%81%97%E3%81%9F%E2%80%A6%E2%80%A6%E3%81%9F%E3%81%B6%E3%82%93%E3%80%82%0A%0A%E4%BB%8A%E3%81%99%E3%81%90%E3%83%97%E3%83%AC%E3%82%A4%EF%BC%81%E2%86%93%0A&url=https://bad-assort.com/play-now/yaminoge/&openExternalBrowser=1' target='_blank' className='block mt-6 px-6 py-3 font-bold text-white bg-[#0f1419] border-none rounded-xl cursor-pointer transition-all duration-300 hover:bg-[#2f3439]'>𝕏でポストする</a>
              <button className="mt-6 px-12 py-4 text-xl font-bold text-white bg-gradient-to-br from-emerald-500 to-emerald-700 border-none rounded-xl cursor-pointer transition-all duration-300 hover:from-emerald-600 hover:to-emerald-800 hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(16,185,129,0.3)]"
                onClick={() => {
                  setDisplayGameResult(false);
                  setDisplayGame(false);
                  setDisplayedBG(0);
                  setWinHistory([]);
                }}>OK</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
